import { useRef, useState, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useSmartPanelPosition, type SmartPanelOptions } from "../../hooks/useSmartPanelPosition";
import type { ReactNode } from "react";

const MARGIN = 16;

interface DraggableEventPanelProps {
  readonly show: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly positionOptions?: SmartPanelOptions;
}

function DraggablePanelContent({
  onClose,
  children,
  positionOptions,
}: {
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly positionOptions?: SmartPanelOptions;
}) {
  const smartPosition = useSmartPanelPosition(positionOptions);
  const panelRef = useRef<HTMLDivElement>(null);

  // Position = smart position + drag offset
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [, setAnchored] = useState(true);
  const dragRef = useRef<{ startX: number; startY: number; origOffsetX: number; origOffsetY: number } | null>(null);

  const didDragRef = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (smartPosition.isMobile) return;
    const target = e.target as HTMLElement;
    if (!target.closest("[data-drag-handle]")) return;
    e.preventDefault();

    didDragRef.current = false;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origOffsetX: offset.x,
      origOffsetY: offset.y,
    };

    const move = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      const dist = Math.hypot(ev.clientX - dragRef.current.startX, ev.clientY - dragRef.current.startY);
      if (dist < 4) return;
      didDragRef.current = true;

      const el = panelRef.current;
      const rawX = dragRef.current.origOffsetX + (ev.clientX - dragRef.current.startX);
      const rawY = dragRef.current.origOffsetY + (ev.clientY - dragRef.current.startY);

      if (el) {
        const vh = window.innerHeight;
        const vw = window.innerWidth;
        const panelH = el.scrollHeight;
        const panelW = el.offsetWidth;
        const idealLeft = smartPosition.left + rawX;
        const idealTop = smartPosition.top + rawY;
        const clampedLeft = Math.max(MARGIN, Math.min(idealLeft, vw - panelW - MARGIN));
        const clampedTop = Math.max(MARGIN, Math.min(idealTop, vh - panelH - MARGIN));
        setOffset({
          x: clampedLeft - smartPosition.left,
          y: clampedTop - smartPosition.top,
        });
      } else {
        setOffset({ x: rawX, y: rawY });
      }
    };

    const up = () => {
      // No drag happened = toggle anchor
      if (!didDragRef.current) {
        setAnchored((prev) => {
          if (prev) return false;
          // Re-anchor: reset offset
          setOffset({ x: 0, y: 0 });
          return true;
        });
      }
      dragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [smartPosition.isMobile, smartPosition.left, smartPosition.top, offset]);

  // Clamp position to viewport after measuring real panel height
  const [clampedPos, setClampedPos] = useState({ left: smartPosition.left, top: smartPosition.top });

  useLayoutEffect(() => {
    if (smartPosition.isMobile) return;
    const el = panelRef.current;
    if (!el) return;

    const clamp = () => {
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      // Use scrollHeight to get the full natural height even if overflow is hidden
      const panelHeight = el.scrollHeight;
      const panelWidth = el.offsetWidth;
      const idealLeft = smartPosition.left + offset.x;
      const idealTop = smartPosition.top + offset.y;

      const left = Math.max(MARGIN, Math.min(idealLeft, vw - panelWidth - MARGIN));
      const top = Math.max(MARGIN, Math.min(idealTop, vh - panelHeight - MARGIN));

      // If panel is taller than viewport, pin to top and allow scroll
      if (panelHeight > vh - MARGIN * 2) {
        el.style.maxHeight = `${vh - MARGIN * 2}px`;
        el.style.overflowY = "auto";
      } else {
        el.style.maxHeight = "";
        el.style.overflowY = "";
      }

      setClampedPos({ left, top });
    };

    clamp();
    const observer = new ResizeObserver(clamp);
    observer.observe(el);
    return () => observer.disconnect();
  }, [smartPosition.left, smartPosition.top, smartPosition.isMobile, offset.x, offset.y]);

  // Mobile + Tablet : full-screen overlay
  if (smartPosition.isMobile) {
    return (
      <>
        <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
        <div className="fixed inset-0 z-50 bg-surface overflow-y-auto scrollbar-custom flex flex-col" data-ph-no-capture>
          <div className="flex-1 min-h-0">{children}</div>
        </div>
      </>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="fixed z-50 w-full max-w-md animate-scale-in"
        style={{ left: clampedPos.left, top: clampedPos.top }}
        onMouseDown={handleMouseDown}
        data-ph-no-capture
      >
        <div className="bg-surface rounded-xl shadow-xl border border-border/40 overflow-hidden relative">
          {children}
        </div>
      </div>
    </>
  );
}

export function DraggableEventPanel({
  show,
  onClose,
  children,
  positionOptions,
}: DraggableEventPanelProps) {
  if (!show) return null;

  const key = `panel-${positionOptions?.formAnchor?.colLeft ?? "center"}-${positionOptions?.formAnchor?.slotTop ?? 0}`;

  return createPortal(
    <DraggablePanelContent
      key={key}
      onClose={onClose}
      positionOptions={positionOptions}
    >
      {children}
    </DraggablePanelContent>,
    document.body
  );
}
